import type { JSX } from 'react';
import { FormGroup } from '@ksyos/design-system';
import type { AssetCategory } from '@common/enums';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { ProcessActivityEditContextMergedProps } from '@frontend/utils';
import { FilesUrlBase } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { AssetsDropzone } from '@frontend/components/elements/dropzone/AssetsDropzone';
import { getSelectedLanguage } from '@frontend/translate';
import {
    useProcessActivityContext,
    useProcessActivityEditContext,
    useProcessActivityEditContextMergedProps,
} from '@frontend/utils';

type ProcessActivityAssetsDropzoneProps = Readonly<{
    acceptExtensions?: string;
    // With `assetCategory` you can create multiple dropzones within a single activity by assigning them different
    // categories. It will be the key used to assign the asset to the correct label in the read schema.
    assetCategory?: AssetCategory;
    error?: string;
    errorText?: string;
    hasErrors?: boolean;
    isRequired?: boolean;
    label: string;
    maxFiles?: number;
    maxSize?: number;
    minFiles?: number;
    onChange: (assets: ProcessActivityAsset[]) => void;
    tooltipText?: string;
    value: ProcessActivityAsset[];
}>;

export function getValidationError(args: {
    error?: string;
    errorText?: string;
    maxFiles?: number;
    minFiles?: number;
}): string | undefined {
    const { error, errorText, maxFiles, minFiles } = args;

    if (errorText) {
        return errorText;
    }

    if (error === 'array_min') {
        if (!minFiles) {
            return getSelectedLanguage().dropzone['array_min_1'];
        }

        return formatString(getSelectedLanguage().dropzone['array_min'], [minFiles.toString()]);
    }

    if (error === 'array_max') {
        if (!maxFiles) {
            return getSelectedLanguage().dropzone['array_max_1'];
        }

        return formatString(getSelectedLanguage().dropzone['array_max'], [maxFiles.toString()]);
    }
}

export function ProcessActivityAssetsDropzone(
    props: ProcessActivityEditContextMergedProps<ProcessActivityAssetsDropzoneProps>
): JSX.Element {
    const {
        acceptExtensions,
        assetCategory,
        error,
        errorText,
        hasErrors,
        isRequired,
        label,
        maxFiles,
        maxSize,
        minFiles,
        onChange,
        value: assets,
        tooltipText,
    } = useProcessActivityEditContextMergedProps<ProcessActivityAssetsDropzoneProps>({
        ...props,
        fieldKey: 'assets',
    });
    const {
        serviceActivity: { isAutoSaveEnabled },
    } = useProcessActivityContext();
    const { processActivityId, setIsUploadingAsset } = useProcessActivityEditContext();

    return (
        <FormGroup
            hasErrors={hasErrors}
            isRequired={isRequired}
            label={label}
            tooltipText={tooltipText}
        >
            <AssetsDropzone
                acceptExtensions={acceptExtensions}
                assets={assets}
                category={assetCategory}
                isAutoSaveEnabled={isAutoSaveEnabled}
                maxAssets={maxFiles}
                maxSize={maxSize}
                onChange={onChange}
                onUploadComplete={() => setIsUploadingAsset(false)}
                onUploadStart={() => setIsUploadingAsset(true)}
                validationError={hasErrors ? getValidationError({ error, errorText, maxFiles, minFiles }) : undefined}
                viewerId={processActivityId}
                viewerUrlBase={FilesUrlBase.processActivities}
            />
        </FormGroup>
    );
}
